import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { BarChart3 } from "lucide-react";
import { EmptyState } from "@/components/shared/EmptyState";
import { Loader } from "@/components/shared/loader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ReportMetricCard } from "@/features/reports/components/ReportMetricCard";
import { ReportSectionError } from "@/features/reports/components/ReportSectionError";
import {
  isReportSectionLoading,
  normalizeReportDateFilters,
} from "@/features/reports/lib/report-query-utils";
import { reportService } from "@/features/reports/services/reportService";
import { handleApiError } from "@/lib/error-handler";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { queryKeys } from "@/lib/query-keys";

interface WebsiteAnalyticsPanelProps {
  from: string;
  to: string;
  onChangeFrom: (value?: string) => void;
  onChangeTo: (value?: string) => void;
}

export function WebsiteAnalyticsPanel({
  from,
  to,
  onChangeFrom,
  onChangeTo,
}: WebsiteAnalyticsPanelProps) {
  const { canManageSettings } = useUserPermissions();
  const canConfigure = canManageSettings;
  const reportQuery = useMemo(
    () => normalizeReportDateFilters(from, to),
    [from, to],
  );

  const ga4Config = useQuery({
    queryKey: queryKeys.reports.websiteAnalyticsConfig(),
    queryFn: () => reportService.getGa4Config(),
  });

  const analytics = useQuery({
    queryKey: queryKeys.reports.websiteAnalytics(reportQuery),
    queryFn: () => reportService.websiteAnalytics(reportQuery),
  });

  const [propertyIdDraft, setPropertyIdDraft] = useState<string | undefined>(
    undefined,
  );
  const [isSaving, setIsSaving] = useState(false);
  const propertyId = propertyIdDraft ?? ga4Config.data?.propertyId ?? "";

  const configLoading = isReportSectionLoading(
    ga4Config.isPending,
    ga4Config.isFetching,
    ga4Config.data,
  );
  const analyticsLoading = isReportSectionLoading(
    analytics.isPending,
    analytics.isFetching,
    analytics.data,
  );

  const saveConfig = async () => {
    setIsSaving(true);
    try {
      await reportService.updateGa4Config(propertyId || undefined);
      await ga4Config.refetch();
      await analytics.refetch();
    } catch (err) {
      handleApiError(err, { customMessage: "تعذّر حفظ إعدادات GA4" });
    } finally {
      setIsSaving(false);
    }
  };

  const engagementPct = analytics.data
    ? `${(analytics.data.engagementRate * 100).toFixed(1)}%`
    : "—";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          من تاريخ
          <input
            type="date"
            value={from}
            onChange={(e) => onChangeFrom(e.target.value || undefined)}
            className="rounded-md border border-border bg-transparent px-2 py-1 text-sm"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          إلى تاريخ
          <input
            type="date"
            value={to}
            onChange={(e) => onChangeTo(e.target.value || undefined)}
            className="rounded-md border border-border bg-transparent px-2 py-1 text-sm"
          />
        </label>
      </div>

      {canConfigure && (
        <Card>
          <CardHeader>
            <CardTitle className="text-right text-base">إعدادات GA4</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-right">
            {ga4Config.error ? (
              <ReportSectionError onRetry={() => ga4Config.refetch()} />
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  أدخل معرّف الخاصية الرقمي من Google Analytics 4. يتطلب الخادم
                  متغير البيئة GA4_SERVICE_ACCOUNT_JSON لجلب البيانات.
                </p>
                <div className="flex flex-wrap items-end justify-end gap-3">
                  <label
                    htmlFor="ga4-property-id"
                    className="flex flex-col gap-1 text-xs text-muted-foreground"
                  >
                    معرّف خاصية GA4
                    <Input
                      id="ga4-property-id"
                      value={propertyId}
                      onChange={(e) => setPropertyIdDraft(e.target.value)}
                      placeholder="123456789"
                      className="w-56"
                      dir="ltr"
                      disabled={configLoading}
                    />
                  </label>
                  <Button
                    size="sm"
                    onClick={saveConfig}
                    disabled={isSaving || configLoading}
                  >
                    {isSaving ? <Loader size="sm" className="ml-1" /> : null}
                    حفظ
                  </Button>
                </div>
                {ga4Config.data && !ga4Config.data.credentialsConfigured && (
                  <p className="text-xs text-amber-600">
                    بيانات الاعتماد غير مضبوطة على الخادم — لن تظهر المقاييس حتى
                    يتم إعداد GA4_SERVICE_ACCOUNT_JSON.
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {analytics.error ? (
        <ReportSectionError onRetry={() => analytics.refetch()} />
      ) : analytics.data?.message && !analytics.data.configured ? (
        <Card>
          <CardContent className="py-8">
            <EmptyState icon={BarChart3} title={analytics.data.message} />
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <ReportMetricCard
              title="الجلسات"
              value={analytics.data?.sessions ?? "—"}
              isLoading={analyticsLoading}
            />
            <ReportMetricCard
              title="مشاهدات الصفحات"
              value={analytics.data?.pageViews ?? "—"}
              valueClassName="text-primary"
              isLoading={analyticsLoading}
            />
            <ReportMetricCard
              title="المستخدمون"
              value={analytics.data?.users ?? "—"}
              isLoading={analyticsLoading}
            />
            <ReportMetricCard
              title="معدل التفاعل"
              value={engagementPct}
              isLoading={analyticsLoading}
            />
          </div>

          {analyticsLoading ? null : (analytics.data?.daily?.length ?? 0) > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-right text-sm">
                  الجلسات اليومية
                </CardTitle>
              </CardHeader>
              <CardContent className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics.data?.daily ?? []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="sessions" fill="hsl(var(--primary))" name="الجلسات" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          ) : null}

          {!analyticsLoading && (analytics.data?.topPages?.length ?? 0) > 0 && (
            <Card className="overflow-hidden text-sm">
              <div className="border-b border-border px-3 py-2 text-right text-xs font-medium text-foreground">
                أكثر الصفحات زيارة
              </div>
              <table className="min-w-full divide-y divide-border text-xs">
                <thead className="bg-muted/30 text-[11px] text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-right">المسار</th>
                    <th className="px-3 py-2 text-center">المشاهدات</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {(analytics.data?.topPages ?? []).map((row) => (
                    <tr key={row.path} className="hover:bg-muted/20">
                      <td className="px-3 py-1.5 text-right font-mono text-[11px]" dir="ltr">
                        {row.path}
                      </td>
                      <td className="px-3 py-1.5 text-center">{row.pageViews}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
